import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const TruckAssignmentPolicy = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const TASK_ID = 17; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "truck_assignment_policy";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey],
          );
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, userId, isAdmin, effectiveUserId]);

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Truck Assignment Policy
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>
      <div
        className="form-wrapper my-4 print-area"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Truck Assignment Policy</h2>
          </div>
          <CompanyAddress />
        </div>

        <h5 className="mt-5">Objective:</h5>
        <p className="mt-4">
          The primary objective of this policy is to ensure the integrity and
          reliability of truck assignments at New Way Transport Inc. This policy
          aims to minimize disruptions to operations caused by unauthorized
          changes in assigned trucks and ensure swift resolution of any
          breakdowns.
        </p>

        <h5 className="mt-5">Responsibilities:</h5>

        <h5 className="mt-5">Maintenance Team:</h5>
        <ul>
          <li>
            Initial Assignments: The Maintenance Team is responsible for
            assigning trucks to drivers according to the provided list.
          </li>
          <li>
            Spare Truck Distribution: Spare trucks may include city trucks,
            trucks utilized during drivers' 36-hour off-duty periods, or
            rotational local trucks. Rotation of spare trucks may occur on a
            daily or periodic basis, ensuring optimal utilization across the
            fleet. Daily spare truck distribution must be managed
          </li>
          <li>
            Temporary Changes: In the event of a breakdown, Maintenance may
            change the assigned truck for one trip or up to three days. However,
            the original truck must be reinstated promptly.
          </li>
          <li>
            Reporting: All truck changes must be reported to Central Maintenance
            and Dispatch promptly.
          </li>
        </ul>

        <h5 className="mt-5">Central Maintenance:</h5>
        <ul>
          <li>
            Data Maintenance: Central Maintenance is responsible for maintaining
            accurate records of all truck assignments and changes. They must
            ensure compliance with all aspects of this policy.
          </li>
        </ul>

        <h5 className="mt-5">Dispatch:</h5>
        <ul>
          <li>
            Posting: Dispatch Supervisors are responsible for ensuring that the
            truck assignment sheet is prominently displayed in front of each
            dispatch area.
          </li>
          <li>
            Updates: Dispatch must promptly update the assignment sheet with any
            changes made.
          </li>
        </ul>

        <h5 className="mt-5">Drivers:</h5>
        <ul>
          <li>
            Awareness: All drivers must be made aware that their assigned trucks
            will not change except in the case of breakdowns.
          </li>
          <li>
            Accountability: Drivers are accountable for maintaining the
            cleanliness of their assigned trucks.
          </li>
        </ul>

        <h5 className="mt-5">Policy Compliance:</h5>
        <ul>
          <li>
            Signatures: Each department head, including Maintenance, Central
            Maintenance, and Dispatch Supervisors, must obtain signatures from
            their team members indicating understanding and agreement to comply
            with this policy.
          </li>
          <li>
            Training: Regular training sessions must be conducted to ensure that
            all personnel are aware of the policy and understand their
            responsibilities.
          </li>
          <li>
            Enforcement: Non-compliance with this policy may result in
            disciplinary action, up to and including termination.
          </li>
        </ul>

        <h5 className="mt-5">Additional Points:</h5>
        <ul>
          <li>
            Spare Truck Usage: Spare trucks are provided temporarily for up to
            three days in the event of a breakdown.
          </li>
          <li>
            Timely Reporting: Any changes to truck assignments must be promptly
            communicated to Central Maintenance and Dispatch.
          </li>
          <li>
            Accountability: Maintenance is responsible for ensuring that drivers
            receive the same truck back after repair.
          </li>
        </ul>

        <p className="mt-5">
          This policy is effective immediately upon issuance. Any suggestions
          for improvement or amendments to this policy should be directed to the
          Management for review and consideration.
        </p>

        <hr className="my-5" />

        <form>
          <div className="row mb-3 mt-5">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="truck_assignment_policy"
              taskId={17}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="truck_assignment_policy"
              taskId={17}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default TruckAssignmentPolicy;
